import { LongDateFormatKey } from 'moment';

import { IFilterConfig } from '../../filter/interfaces';
import { TableColumn } from '../../shared/interfaces/table.interface';

const frequencyList = [
  { label: 'One-time', value: 'one_time' },
  { label: 'Recurring', value: 'recurring' },
];
const paymentTermList = [
  { label: '100% Down', value: '100% Down' },
  { label: '50/50', value: '50/50' },
  { label: '35/65', value: '35/65' },
  { label: '30/70', value: '30/70' },
  { label: '20/80', value: '20/80' },
  { label: '10/90', value: '10/90' },
  { label: 'Paid on Delivery', value: 'Paid on Delivery' },
  { label: 'Net 15', value: 'Net 15' },
  { label: 'Net 20', value: 'Net 20' },
  { label: 'Net 25', value: 'Net 25' },
  { label: 'Net 30', value: 'Net 30' },
  { label: 'Net 45', value: 'Net 45' },
  { label: 'Net 60', value: 'Net 60' },
  { label: 'Due on Receipt', value: 'Due on Receipt' },
  { label: 'Custom Term', value: 'Custom Term' },
  { label: 'Consignment', value: 'Consignment' },
  {
    label: '25% Down, Net 30 Upon Delivery',
    value: '25% Down, Net 30 Upon Delivery',
  },
  {
    label: '25% Down, Net 45 Upon Delivery',
    value: '25% Down, Net 45 Upon Delivery',
  },
  {
    label: '25% Down, Net 60 Upon Delivery',
    value: '25% Down, Net 60 Upon Delivery',
  },
  { label: '25% Down, Net 90', value: '25% Down, Net 90' },
  { label: 'Due on Delivery', value: 'Due on Delivery' },
];
const deliveryTypeList = [
  { label: 'Standard', value: 'standard' },
  { label: 'Warehouse Pickup', value: 'warehouse' },
  { label: 'Local Delivery', value: 'local_delivery' },
];

const lineItemfilterList = [
  {
    value: 'sku',
    label: 'SKU',
  },
  {
    value: 'product_name',
    label: 'Product Name',
  },
  {
    value: 'tags',
    label: 'Tags',
  },
  {
    value: 'Category',
    label: 'category',
  },
  {
    value: 'sub_category',
    label: 'Sub-Category',
  },
  {
    value: 'product_type',
    label: 'Product Type',
  },
];

const lineItemHeader :TableColumn<any>[] = [
  {
    label: '',
    property: 'id',
    type: 'checkbox',
    visible: true,
    cssClasses: [],
  },
  {
    label: 'Image',
    property: 'image',
    type: 'image',
    visible: true,
    cssClasses: [],
  },
  {
    label: 'SKU',
    property: 'sku',
    type: 'text',
    visible: true,
    cssClasses: [],
  },
  {
    label: 'Product Name',
    property: 'product_name',
    type: 'text',
    visible: true,
    cssClasses: [],
  },
  {
    label: 'Status',
    property: 'status',
    type: 'chip',
    visible: true,
    cssClasses: (status :string) :string[] => {
      switch (status) {
        case 'available':
          return [
            'text-purple-700 bg-purple-200 hover:purple-300 active:purple-400 focus:purple-300',
          ];
        case 'unavailable':
          return [
            'text-red-800',
            'bg-red-200',
            'hover:red-400',
            'active:red-500',
            'focus:red-400',
          ];
        default:
          return [''];
      }
    },
    multiple: true,
  },
  {
    label: 'Category',
    property: 'category',
    type: 'text',
    visible: true,
    cssClasses: [],
  },
  {
    label: 'Sub Category',
    property: 'sub_category',
    type: 'text',
    visible: true,
    cssClasses: [],
  },
  {
    label: 'Tags',
    property: 'tags',
    type: 'chip',
    visible: true,
    cssClasses: () :string[] => {
      return [
        'text-purple-700',
        'bg-purple-200',
        'hover:purple-300',
        'active:purple-400',
        'focus:purple-300',
      ];
    },
    multiple: true,
  },
  {
    label: 'QTY',
    property: 'quantity',
    type: 'text',
    visible: true,
    cssClasses: [],
  },
];

const dateInput :LongDateFormatKey = 'L';
const dateFormat = {
  parse: {
    dateInput,
  },
  display: {
    dateInput,
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

const lineItemFiltersConfig :IFilterConfig[] = [
  {
    id: 'sku',
    type: 'input',
    label: 'SKU',
    query: 'sku',
    show: true,
  },
  {
    id: 'categoryId',
    type: 'category',
    label: 'Category',
    query: 'categoryId',
    show: true,
    config: {},
  },
  {
    id: 'subCategoryId',
    type: 'category',
    label: 'Subcategory',
    query: 'subCategoryId',
    show: true,
    config: {
      isSubcategory: true,
    },
  },
  {
    id: 'tags',
    type: 'tags',
    label: 'Tags',
    query: 'tags',
    show: true,
    config: {
      inlineChips: false,
    },
  },
  {
    id: 'inStockOnly',
    type: 'checkbox',
    label: 'In Stock Only',
    query: 'inStockOnly',
    show: true,
  },
  {
    id: 'hasPurchased',
    type: 'checkbox',
    label: 'Has Purchased',
    query: 'hasPurchased',
    show: false,
    config: { default: 0 },
  },
  {
    id: 'lastPurchaseDate',
    type: 'date',
    label: 'Last Purchase Date',
    query: 'lastPurchaseDate',
    show: false,
  },
];

const shipStationRequiredFields = {
  contact: [
    'shipping_email',
    'shipping_phone',
    'shipping_name',
    'shipping_company',
  ],
  shipping: ['ship_address', 'ship_city', 'ship_state', 'ship_country', 'ship_zip'],
};

export const InvoiceConfig = {
  frequencyList,
  paymentTermList,
  deliveryTypeList,
  lineItemfilterList,
  lineItemHeader,
  dateFormat,
  lineItemFiltersConfig,
  shipStationRequiredFields,
};
